import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { FieldValues, UseFormTrigger } from 'react-hook-form';
import { FieldPath } from 'react-hook-form/dist/types/path';
import { once } from 'lodash';
import { getChildrenNames } from '@/components/Form/FormUtils';
import { ButtonsProps } from '@/components/Button';

type SectionInputList<T extends FieldValues> = FieldPath<T>[];
type Sections<T extends FieldValues> = {
  [key: number]: SectionInputList<T>;
};

type FormContextProps<T extends FieldValues> = {
  sections?: Sections<T>;
  sectionsCount?: number;
  pageCount?: number;
  paginations?: { [page: string]: number };
  currentSection?: number;
  goNext?: () => void;
  goPrev?: () => void;
  setNextButtonLabel?: (value?: string) => void;
  setPrevButtonLabel?: (value?: string) => void;
  nextButtonLabel?: string;
  prevButtonLabel?: string;
  setNextButtonProps?: (value?: Omit<ButtonsProps, 'children'>) => void;
  setPrevButtonProps?: (value?: Omit<ButtonsProps, 'children'>) => void;
  nextButtonProps?: Omit<ButtonsProps, 'children'>;
  prevButtonProps?: Omit<ButtonsProps, 'children'>;
  setShowPagination?: (value?: boolean) => void;
  showPagination?: boolean;
};

const createFormSectionContext = once(<T extends FieldValues>() =>
  createContext<FormContextProps<T>>({})
);

const getSectionFieldNames = <T extends FieldValues>(
  children: React.ReactNode,
  sectionIndex?: number
) => {
  let array: Sections<T> = {};
  (Array.isArray(children) ? [...children] : [children]).forEach((child: React.ReactElement) => {
    if (typeof sectionIndex !== 'undefined' && typeof array[sectionIndex] === 'undefined') {
      array[sectionIndex] = [];
    }
    if (child === null || child === undefined) {
      return;
    }
    if (child.type === FormSection) {
      if (typeof array[child.props.sectionOrder] === 'undefined') {
        array[child.props.sectionOrder] = [];
      }
      array = { ...array, ...getSectionFieldNames(child.props.children, child.props.sectionOrder) };
    }
    if (child?.props?.name && child?.props?.name !== '') {
      if (sectionIndex) {
        array[sectionIndex].push(child?.props?.name);
      }
      if (child?.props?.accessoryElement) {
        array = { ...array, ...getSectionFieldNames(child?.props?.accessoryElement, sectionIndex) };
      }
    }
    if (child?.props?.children) {
      array = { ...array, ...getSectionFieldNames(child?.props?.children, sectionIndex) };
    }
    if (Array.isArray(child)) {
      array = { ...array, ...getSectionFieldNames(child, sectionIndex) };
    }
  });
  return array;
};

const getPaginations = (children: React.ReactNode) => {
  let array: { [page: string]: number } = {};
  (Array.isArray(children) ? [...children] : [children]).forEach((child: React.ReactElement) => {
    if (child === null || child === undefined) {
      return;
    }
    if (child.type === FormSection) {
      if (child.props.countInPagination === true || child.props.countInPagination === undefined) {
        array[child.props.sectionOrder] = 1;
      }
    }
    if (child?.props?.children) {
      array = { ...array, ...getPaginations(child?.props?.children) };
    }
    if (Array.isArray(child)) {
      array = { ...array, ...getPaginations(child) };
    }
  });
  let page = 1;
  const ordered = Object.keys(array)
    .sort()
    .reduce((obj: { [page: string]: number }, key) => {
      obj[key] = page;
      page += 1;
      return obj;
    }, {});

  return ordered;
};

const FormSectionProvider = <T extends FieldValues>({
  children,
  trigger,
  defaultSection = 1,
}: {
  children: React.ReactNode;
  trigger: UseFormTrigger<T>;
  defaultSection?: number;
}) => {
  const FormSectionContext = createFormSectionContext<T>();
  const [currentSection, setCurrentSection] = useState<number>(defaultSection);
  const [nextButtonLabel, setNextButtonLabel] = useState<string | undefined>();
  const [prevButtonLabel, setPrevButtonLabel] = useState<string | undefined>();
  const [nextButtonProps, setNextButtonProps] = useState<
    Omit<ButtonsProps, 'children'> | undefined
  >();
  const [prevButtonProps, setPrevButtonProps] = useState<
    Omit<ButtonsProps, 'children'> | undefined
  >();
  const [showPagination, setShowPagination] = useState<boolean | undefined>();

  const [sections, setSections] = useState<Sections<T>>([]);
  const [paginations, setPaginations] = useState<{ [page: string]: number }>({});

  const currentChildrenNames = getChildrenNames(children);
  useEffect(() => {
    const sectionsInternal = getSectionFieldNames<T>(children);
    setSections(sectionsInternal);
    const paginationsInternal = getPaginations(children);
    setPaginations(paginationsInternal);
    return () => {};
  }, [JSON.stringify(currentChildrenNames)]);

  useEffect(() => {
    setCurrentSection(defaultSection);
  }, [defaultSection]);

  const values = useMemo(() => {
    return {
      sections,
      currentSection,
      goNext:
        currentSection < Object.keys(sections).length
          ? async () => {
              const ok = await trigger(sections[currentSection]);
              if (ok) {
                setCurrentSection(currentSection + 1);
              }
            }
          : undefined,
      goPrev:
        currentSection > 1
          ? () => {
              setCurrentSection(currentSection - 1);
            }
          : undefined,
      sectionsCount: Object.keys(sections).length,
      pageCount: Object.keys(paginations).length,
      paginations,
      setNextButtonLabel,
      setPrevButtonLabel,
      nextButtonLabel,
      prevButtonLabel,
      setNextButtonProps,
      setPrevButtonProps,
      nextButtonProps,
      prevButtonProps,
      setShowPagination,
      showPagination,
    };
  }, [
    currentSection,
    JSON.stringify(sections),
    JSON.stringify(paginations),
    nextButtonLabel,
    prevButtonLabel,
    nextButtonProps,
    prevButtonProps,
    showPagination,
  ]);

  return <FormSectionContext.Provider value={values}>{children}</FormSectionContext.Provider>;
};

const useFormSectionContext = <T extends FieldValues>() =>
  useContext(createFormSectionContext<T>());

type FormSectionProps = {
  children: React.ReactNode;
  sectionOrder: number;
  nextButtonLabel?: string;
  nextButtonProps?: Omit<ButtonsProps, 'children'>;
  prevButtonLabel?: string;
  prevButtonProps?: Omit<ButtonsProps, 'children'>;
  countInPagination?: boolean;
};

const FormSection = (props: FormSectionProps) => {
  const {
    currentSection,
    setNextButtonLabel,
    setPrevButtonLabel,
    setNextButtonProps,
    setPrevButtonProps,
    setShowPagination,
  } = useFormSectionContext();
  const {
    children,
    sectionOrder = false,
    countInPagination = true,
    nextButtonLabel,
    nextButtonProps,
    prevButtonLabel,
    prevButtonProps,
  } = props;

  useEffect(() => {
    if (sectionOrder === currentSection && setNextButtonLabel) {
      setNextButtonLabel(nextButtonLabel);
    }
  }, [nextButtonLabel, currentSection, sectionOrder]);

  useEffect(() => {
    if (sectionOrder === currentSection && setPrevButtonLabel) {
      setPrevButtonLabel(prevButtonLabel);
    }
  }, [prevButtonLabel, currentSection, sectionOrder]);

  useEffect(() => {
    if (sectionOrder === currentSection && setNextButtonProps) {
      setNextButtonProps(nextButtonProps);
    }
  }, [nextButtonLabel, currentSection, sectionOrder]);

  useEffect(() => {
    if (sectionOrder === currentSection && setPrevButtonProps) {
      setPrevButtonProps(prevButtonProps);
    }
  }, [prevButtonLabel, currentSection, sectionOrder]);

  useEffect(() => {
    if (sectionOrder === currentSection && setShowPagination) {
      setShowPagination(countInPagination);
    }
  }, [countInPagination, currentSection, sectionOrder]);

  if (sectionOrder !== currentSection) {
    return null;
  }
  return children;
};

export { useFormSectionContext, FormSectionProvider, FormSection };
